import type { ResourceStockpile } from '../core/economyTypes'
import type { TechnologyId } from './technologies'

export type SpaceMissionId =
  | 'first-satellite'
  | 'crewed-orbit'
  | 'lunar-validation'
  | 'crewed-lunar-landing-return'

export interface SpaceMissionDefinition {
  id: SpaceMissionId
  order: 1 | 2 | 3 | 4
  name: string
  shortName: string
  description: string
  preparationTurns: number
  cost: ResourceStockpile
  requiredTechnologies: TechnologyId[]
  prerequisiteMission: SpaceMissionId | null
  isVictoryMission: boolean
}

export const SPACE_MISSION_DEFINITIONS: SpaceMissionDefinition[] = [
  {
    id: 'first-satellite',
    order: 1,
    name: '首颗人造卫星入轨',
    shortName: '人造卫星',
    description: '验证运载火箭、轨道计算与地面测控的首次完整闭环。',
    preparationTurns: 2,
    cost: { money: 40, metal: 10, fuel: 18, electronics: 8 },
    requiredTechnologies: ['multistage-rockets', 'orbital-flight'],
    prerequisiteMission: null,
    isVictoryMission: false,
  },
  {
    id: 'crewed-orbit',
    order: 2,
    name: '载人地球轨道飞行',
    shortName: '载人轨道',
    description: '验证载人飞船、生命保障、制导与安全返回能力。',
    preparationTurns: 3,
    cost: { money: 60, metal: 18, fuel: 24, electronics: 12 },
    requiredTechnologies: ['crewed-spacecraft'],
    prerequisiteMission: 'first-satellite',
    isVictoryMission: false,
  },
  {
    id: 'lunar-validation',
    order: 3,
    name: '载人绕月与登月流程验证',
    shortName: '绕月验证',
    description: '执行载人月球航程并验证登月任务所需的关键流程。',
    preparationTurns: 4,
    cost: { money: 90, metal: 28, fuel: 38, electronics: 18 },
    requiredTechnologies: ['crewed-spacecraft', 'multistage-rockets'],
    prerequisiteMission: 'crewed-orbit',
    isVictoryMission: false,
  },
  {
    id: 'crewed-lunar-landing-return',
    order: 4,
    name: '载人登月并安全返回',
    shortName: '载人登月',
    description: '完成月面着陆、离月、再入与安全返回，达成航天胜利。',
    preparationTurns: 5,
    cost: { money: 130, metal: 40, fuel: 55, electronics: 24 },
    requiredTechnologies: ['crewed-spacecraft', 'inertial-guidance'],
    prerequisiteMission: 'lunar-validation',
    isVictoryMission: true,
  },
]

export function getSpaceMissionDefinition(
  missionId: SpaceMissionId,
): SpaceMissionDefinition {
  const definition = SPACE_MISSION_DEFINITIONS.find(
    (mission) => mission.id === missionId,
  )

  if (!definition) {
    throw new Error(`Unknown space mission: ${missionId}`)
  }

  return definition
}

export function isSpaceMissionId(value: unknown): value is SpaceMissionId {
  return SPACE_MISSION_DEFINITIONS.some((mission) => mission.id === value)
}
